import { Building2, Crown, KeyRound, Mail, Plus, Trash2, TriangleAlert, Unlink, UserPlus, Users } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  api,
  type OrganizationUpdate,
  type OrgTenant,
  type Tenant,
} from "../../../api/client.ts";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { useOrgInvitations, useOrgMembers, useOrgTenants, useTenants } from "../../../hooks/resources.ts";
import { ORG_ROLES, orgRoleLabel } from "../../../auth/orgRoles.ts";
import { keys } from "../../../api/queryKeys.ts";
import FormattedDate from "../../../components/FormattedDate";
import { TenantCell } from "../../../components/IdentityCell.tsx";
import OrgInviteDialog from "../../../components/OrgInviteDialog.tsx";
import { OrgInvitationRows } from "../../../components/OrgInvitationsList.tsx";
import OrgMembersTable from "../../../components/OrgMembersTable";
import OwnershipChip from "../../../components/OwnershipChip.tsx";
import RowActions from "../../../components/RowActions.tsx";
import SettingRow from "../../../components/SettingRow.tsx";
import TenantDialog from "../../../components/TenantDialog.tsx";
import AccordionCard from "../../../components/AccordionCard.tsx";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import DetailPageStatus from "../../../components/detail/DetailPageStatus.tsx";
import OrganizationFeaturesPanel from "./OrganizationFeaturesPanel.tsx";
import { formatNumber } from "../../../utils/format.ts";

const plural = (n: number, one: string, many: string) => `${formatNumber(n)} ${n === 1 ? one : many}`;

const BACK = { to: "/settings/organizations", label: "Organizations" };

function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [assignTenantOpen, setAssignTenantOpen] = useState(false);
  const [assignTenantId, setAssignTenantId] = useState("");
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [deleteOrgOpen, setDeleteOrgOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoDomain, setSsoDomain] = useState("");
  const [ssoDefaultRole, setSsoDefaultRole] = useState("member");
  const [ssoError, setSsoError] = useState<string | null>(null);

  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: keys.organization.detail(id ?? ""),
    queryFn: () => api.getOrganization(id!),
    enabled: !!id,
  });

  const { data: orgTenants = [], isLoading: tenantsLoading } = useOrgTenants(id);
  // Same cache entry OrgMembersTable reads; only the count is needed here.
  const { data: members } = useOrgMembers(id);
  const { data: invitations = [] } = useOrgInvitations(id);

  const { data: allTenants = [] } = useTenants(undefined, {
    enabled: assignTenantOpen,
  });

  const assignTenantMutation = useMutation({
    mutationFn: () => api.assignOrgTenant(id!, { tenant_id: assignTenantId }),
    onSuccess: () => {
      // .all: other orgs linked to the tenant see a changed shared_with.
      queryClient.invalidateQueries({ queryKey: keys.organizationTenant.all });
      setAssignTenantOpen(false);
      setAssignTenantId("");
      toast.success("Tenant assigned");
    },
  });

  const unassignTenantMutation = useMutation({
    mutationFn: (tenantId: string) => api.unassignOrgTenant(id!, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.organizationTenant.all });
      toast.success("Tenant unassigned");
    },
  });

  const makePrimaryMutation = useMutation({
    mutationFn: (ot: OrgTenant) =>
      api.setTenantPrimaryOrganization(ot.primary_organization?.id ?? id!, ot.tenant_id, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.organizationTenant.all });
      toast.success("Organization is now the tenant's owner");
    },
  });

  const resetSso = useCallback(() => {
    if (!org) return;
    setSsoEnabled(org.google_sso_enabled);
    setSsoDomain(org.google_workspace_domain ?? "");
    setSsoDefaultRole(org.google_default_role || "member");
    setSsoError(null);
  }, [org]);

  useEffect(resetSso, [resetSso]);

  const ssoMutation = useMutation({
    mutationFn: (data: OrganizationUpdate) => api.updateOrganization(id!, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.organization.detail(id!), updated);
      queryClient.invalidateQueries({ queryKey: keys.organization.all });
      setSsoError(null);
      toast.success("SSO settings saved");
    },
    onError: (err: unknown) => {
      const detail =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Failed to save SSO settings";
      setSsoError(detail);
    },
    meta: { suppressErrorToast: true },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: () => api.deleteOrganization(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.organization.all });
      navigate("/settings/organizations");
      toast.success("Organization deleted");
    },
  });

  const availableTenants = allTenants.filter(
    (t: Tenant) => !orgTenants.some((ot: OrgTenant) => ot.tenant_id === t.id),
  );

  if (orgLoading || !org) {
    return (
      <DetailPageStatus isLoading={orgLoading} message="Organization not found." back={BACK} />
    );
  }

  const ssoDirty =
    ssoEnabled !== org.google_sso_enabled ||
    ssoDomain.trim() !== (org.google_workspace_domain ?? "") ||
    ssoDefaultRole !== (org.google_default_role || "member");
  const expiredInvitations = invitations.filter((inv) => new Date(inv.expires_at).getTime() <= Date.now()).length;
  const tenantSummary =
    orgTenants.length === 0
      ? "Members see an empty account until a tenant is assigned"
      : orgTenants.length <= 3
        ? orgTenants.map((ot) => ot.tenant_name).join(", ")
        : `${orgTenants
            .slice(0, 3)
            .map((ot) => ot.tenant_name)
            .join(", ")} +${formatNumber(orgTenants.length - 3)}`;

  const tenantCount = plural(orgTenants.length, "tenant", "tenants");
  const memberCount = members ? ` · ${plural(members.length, "member", "members")}` : "";

  return (
    <Box>
      <PageHeader
        back={BACK}
        title={org.name}
        description={`Sign-in, features, tenants and members of this organization · ${tenantCount}${memberCount}`}
        actions={
          <Button variant="contained" startIcon={<UserPlus />} onClick={() => setInviteOpen(true)}>
            Invite people
          </Button>
        }
      />
      <Stack spacing={1.5}>
        <AccordionCard
          logo={<Users sx={{ color: "text.secondary" }} />}
          title="Members"
          count={members?.length}
          summary={`Who can access ${org.name} and what they can do`}
          defaultExpanded
          disablePadding
        >
          <OrgMembersTable organizationId={id!} canManage canManageGroups flush />
        </AccordionCard>
        {invitations.length > 0 && (
          <AccordionCard
            logo={<Mail sx={{ color: "text.secondary" }} />}
            title="Pending invitations"
            count={invitations.length}
            summary={[
              `${formatNumber(invitations.length - expiredInvitations)} waiting`,
              expiredInvitations > 0 && `${formatNumber(expiredInvitations)} expired`,
            ]
              .filter(Boolean)
              .join(" · ")}
            status={
              expiredInvitations > 0
                ? { kind: "attention", label: `${formatNumber(expiredInvitations)} expired` }
                : undefined
            }
            disablePadding
          >
            <OrgInvitationRows organizationId={id!} invitations={invitations} />
          </AccordionCard>
        )}
        <AccordionCard
          logo={<Building2 sx={{ color: "text.secondary" }} />}
          title="Tenants"
          count={orgTenants.length}
          summary={tenantSummary}
          status={!tenantsLoading && orgTenants.length === 0 ? { kind: "attention", label: "No tenant" } : undefined}
          disablePadding
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "flex-end", px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
          >
            <Button size="small" variant="outlined" onClick={() => setAssignTenantOpen(true)}>
              Assign existing tenant
            </Button>
            <Button size="small" variant="contained" startIcon={<Plus />} onClick={() => setCreateTenantOpen(true)}>
              Create tenant
            </Button>
          </Stack>
          {!tenantsLoading && orgTenants.length === 0 ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              This organization has no tenant. Its members can log in but will see an empty account with no
              data — onboarding is incomplete. Create a tenant to fix it.
            </Alert>
          ) : (
            <ScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {orgTenants.map((ot: OrgTenant) => (
                  <TableRow key={ot.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell>
                      <TenantCell name={ot.tenant_name} />
                    </TableCell>
                    <TableCell>
                      <OwnershipChip link={ot} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                      <FormattedDate value={ot.created_at} />
                    </TableCell>
                    <TableCell padding="checkbox" align="right">
                      <RowActions
                        label={ot.tenant_name}
                        actions={[
                          {
                            label: "Make primary",
                            icon: <Crown fontSize="small" />,
                            hidden: ot.is_primary,
                            disabled: makePrimaryMutation.isPending,
                            onClick: () => makePrimaryMutation.mutate(ot),
                          },
                          {
                            label: "Unassign tenant",
                            icon: <Unlink fontSize="small" />,
                            destructive: true,
                            onClick: () => unassignTenantMutation.mutateAsync(ot.tenant_id),
                            confirm: {
                              title: "Unassign tenant",
                              confirmLabel: "Unassign",
                              body: (
                                <>
                                  Unassign <strong>{ot.tenant_name}</strong> from {org.name}? The tenant and
                                  its data stay; this organization's members lose access to it.
                                </>
                              ),
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
          )}
        </AccordionCard>
        <AccordionCard
          logo={<KeyRound sx={{ color: "text.secondary" }} />}
          title="Single sign-on"
          summary={
            org.google_sso_enabled
              ? [
                  "Google Workspace",
                  org.google_workspace_domain,
                  `joins as ${orgRoleLabel(org.google_default_role || "member")}`,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "Google Workspace sign-in is off"
          }
          status={org.google_sso_enabled ? { kind: "ok", label: "On" } : { kind: "off", label: "Off" }}
          disablePadding
        >
          <SettingRow
            label="Google Workspace SSO"
            description="People with an email on the domain below join this organization when they sign in with Google"
            action={
              <Switch
                checked={ssoEnabled}
                onChange={(e) => setSsoEnabled(e.target.checked)}
                slotProps={{ input: { "aria-label": "Enable Google Workspace SSO" } }}
              />
            }
          />
          <Box sx={{ px: 2, pb: 2, pt: 1 }}>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
              <TextField
                label="Google Workspace domain"
                placeholder="acme.com"
                value={ssoDomain}
                onChange={(e) => setSsoDomain(e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Default role for new members</InputLabel>
                <Select
                  value={ssoDefaultRole}
                  label="Default role for new members"
                  onChange={(e) => setSsoDefaultRole(e.target.value)}
                  renderValue={(v) => orgRoleLabel(v)}
                >
                  {ORG_ROLES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      <ListItemText primary={r.label} secondary={r.description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {ssoError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {ssoError}
              </Alert>
            )}
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", mt: 2 }}>
              {ssoDirty && (
                <Button onClick={resetSso} disabled={ssoMutation.isPending}>
                  Discard
                </Button>
              )}
              <Button
                variant="contained"
                loading={ssoMutation.isPending}
                disabled={!ssoDirty}
                onClick={() =>
                  ssoMutation.mutate({
                    google_sso_enabled: ssoEnabled,
                    google_workspace_domain: ssoDomain.trim() || null,
                    google_default_role: ssoDefaultRole,
                  })
                }
              >
                Save
              </Button>
            </Stack>
          </Box>
        </AccordionCard>
        <OrganizationFeaturesPanel org={org} />
        <AccordionCard
          logo={<TriangleAlert sx={{ color: "error.main" }} />}
          title="Danger zone"
          summary="Delete this organization"
          disablePadding
        >
          <SettingRow
            label="Delete organization"
            description="Removes all member access and tenant assignments. Tenants and their data stay."
            action={
              <Button color="error" variant="outlined" startIcon={<Trash2 />} onClick={() => setDeleteOrgOpen(true)}>
                Delete organization
              </Button>
            }
          />
        </AccordionCard>
      </Stack>
      <Dialog
        open={assignTenantOpen}
        onClose={() => setAssignTenantOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign existing tenant</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={assignTenantId}
              onChange={(e) => setAssignTenantId(e.target.value)}
              label="Tenant"
            >
              {availableTenants.map((t: Tenant) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTenantOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => assignTenantMutation.mutate()}
            loading={assignTenantMutation.isPending}
            disabled={!assignTenantId}
          >
            Assign tenant
          </Button>
        </DialogActions>
      </Dialog>
      <OrgInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        organizationId={id!}
        organizationName={org.name}
      />
      {createTenantOpen && (
        <TenantDialog onClose={() => setCreateTenantOpen(false)} organizationId={id} />
      )}
      <ConfirmDialog
        open={deleteOrgOpen}
        title="Delete organization"
        pending={deleteOrgMutation.isPending}
        onConfirm={() => deleteOrgMutation.mutate()}
        onClose={() => setDeleteOrgOpen(false)}
      >
        Delete <strong>{org.name}</strong>? This will remove all member access and tenant assignments.
      </ConfirmDialog>
    </Box>
  );
}

export default OrganizationDetailPage;
